import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
  Card as ChakraCard,
  Tooltip,
  Icon,
} from '@chakra-ui/react'
import {
  Dispatch,
  SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { debounce } from 'lodash'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import Card from '../../../common/components/Card'
import UpgradeBadge from '../../../components/UpgradeBadge'
import { usePaywall } from '../../../hooks/usePaywall'
import {
  ProductResult,
  ProductSearchClient,
  resetProductResults,
  selectProduct,
  setProductResults,
  setSearchTerm,
} from '../ScriptForm/ProductSearchContextSlice'
import {
  resetPrescribedItemByIndex,
  selectPrescribedItem,
} from '../ScriptForm/PrescribedItem/PrescribedItemSlice'
import { DrugSearchResultsPanel } from '../DrugSearchResultsPanel'
import useApi from '../../../common/utils/api/useApi'
import { FormControl, FormLabel } from '@chakra-ui/form-control'
import {
  customPrescriptionItemToPrescribedItem,
  detailsToPrescribedItem,
} from '../ScriptForm/PrescribedItem'
import { PrescribedItem } from '../../../types'
import { CustomPrescriptionItemModal } from '../CustomPrescriptionItem/CustomPrescriptionItemModal'
import { initFormError } from '../constants'
import MimsIntegrated from '../../../assets/mims-integrated.png'
import { preCheckProduct } from '../ScriptForm/PrescribedItem/utils/preCheckProduct'
import PrescribedItemDetails from './components/PrescribedItemDetails'
import { getPbsStatus } from './utils/getPbsStatus'
import { PrescribedItemErrors } from '../../../types/prescription'
import { RootState } from '../../../app/store'
import { AddIcon, SmallCloseIcon, WarningIcon } from '@chakra-ui/icons'
import { useDispatch } from 'react-redux'
import { FundingSource } from '../../../types/organization'
import { useHasEntitlement } from '../../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../../types/organization-entitlements'

const DEFAULT_PAGE_INFO = { pageNumber: 1, pageOffset: 30 }

interface PrescriptionListProps {
  scid?: string
  prescribedItems: (PrescribedItem | undefined)[]
  toggleDrawer?: () => void
  onDelete?: (index: number) => void
  onAdd: (item?: PrescribedItem) => any
  onUpdate: (item: PrescribedItem, index: number) => any
  formErrors: Record<string, PrescribedItemErrors>
  setFormErrors: Dispatch<SetStateAction<Record<string, PrescribedItemErrors>>>
  isAbleToSelectS8Medicine: boolean
  isOnlyPrivateMedicationAllowed: boolean
}

const PrescribedItemList = forwardRef<HTMLDivElement, PrescriptionListProps>((props, ref) => {
  const { prescribedItems, onAdd, onDelete, formErrors } = props
  const tabRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const { selectedPrescribedItemIndex } = useAppSelector((state) => state.newScript)
  const isMultipleScriptsEnabled = useHasEntitlement(FeatureKey.MULTIPLE_SCRIPTS)
  const { hasSubscriptionError, open: openPaywall, paywallModal } = usePaywall()

  const shouldShowPaywall =
    hasSubscriptionError || (!isMultipleScriptsEnabled && prescribedItems.length >= 1)

  const handleAddMedication = () => {
    if (shouldShowPaywall) {
      openPaywall()
      return
    }
    onAdd()
    dispatch(selectPrescribedItem(prescribedItems.length))

    setTimeout(() => {
      tabRef.current?.scrollTo({ left: tabRef.current?.scrollWidth, behavior: 'smooth' })
    }, 100)
  }

  const handleTabChange = (index: number) => {
    if (index === prescribedItems.length) {
      handleAddMedication()
      return
    }
    dispatch(selectPrescribedItem(index))
  }

  const handleDeleteScript = () => {
    onDelete?.(selectedPrescribedItemIndex)
    const newLength = prescribedItems.length - 1
    dispatch(
      selectPrescribedItem(
        selectedPrescribedItemIndex <= newLength - 1
          ? selectedPrescribedItemIndex
          : Math.max(0, selectedPrescribedItemIndex - 1)
      )
    )
  }

  return (
    <VStack ref={ref} alignItems="stretch">
      <Heading as="h4" size="md">
        Prescription Summary
      </Heading>
      {paywallModal}
      <Card p="16px">
        <Tabs
          variant="enclosed-colored"
          onChange={handleTabChange}
          index={selectedPrescribedItemIndex}
        >
          <Flex
            maxWidth="100%"
            overflow="auto"
            h="62px"
            alignItems="end"
            ref={tabRef}
            overflowY="hidden"
            pb="2px"
          >
            <TabList>
              {prescribedItems.map((item, index) => (
                <Tooltip label={item?.displayName}>
                  <Tab
                    key={index}
                    whiteSpace="nowrap"
                    role="group"
                    _hover={{
                      '& .tab-close-icon': {
                        visibility: 'visible',
                      },
                    }}
                    display="flex"
                    alignItems="center"
                    maxHeight="100%"
                  >
                    {item?.productId &&
                      (formErrors?.[item.productId]?.patientInstructions?.showError ||
                        formErrors?.[item.productId]?.reasonForPrescribing?.showError ||
                        formErrors?.[item.productId]?.quantity?.showError ||
                        formErrors?.[item.productId]?.pbsAuthority?.showError) && (
                        <Icon as={WarningIcon} color="red.500" mr={2} />
                      )}
                    <Text maxW="120px" overflow="hidden" textOverflow="ellipsis">
                      {item?.displayName || `Medication ${index + 1}`}
                    </Text>
                    {prescribedItems.length > 1 && (
                      <SmallCloseIcon
                        ml={2}
                        visibility="hidden"
                        className="tab-close-icon"
                        _hover={{
                          backgroundColor: 'rgba(249, 112, 112, 0.1)',
                          borderRadius: 'full',
                          cursor: 'pointer',
                          color: 'red.500',
                        }}
                        onClick={() => handleDeleteScript()}
                      />
                    )}
                  </Tab>
                </Tooltip>
              ))}
              {prescribedItems.length < 15 && (
                <Tab whiteSpace="nowrap" position="relative">
                  <AddIcon boxSize={3} />
                  <Text ml={2}>Add Medication</Text>
                  {shouldShowPaywall && <UpgradeBadge />}
                </Tab>
              )}
            </TabList>
          </Flex>
          <TabPanels>
            {prescribedItems.map((item, index) => (
              <TabPanel key={index}>
                <AddDrugDetails {...props} medication={item} />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card>
    </VStack>
  )
})

PrescribedItemList.displayName = 'PrescribedItemList'

const AddDrugDetails: React.FC<
  PrescriptionListProps & {
    medication: PrescribedItem | undefined
  }
> = ({
  scid,
  toggleDrawer,
  prescribedItems,
  onUpdate,
  formErrors,
  setFormErrors,
  isAbleToSelectS8Medicine,
  isOnlyPrivateMedicationAllowed,
  medication,
}) => {
  const { productResults, productSearchTerm, selectedProduct } = useAppSelector(
    (state) => state.product
  )
  const { selectedPrescribedItemIndex } = useAppSelector((state) => state.newScript)
  const isDeactivatedPrescriber: boolean = useAppSelector(
    ({ prescriber }: RootState) => !prescriber?.prescriber?.active
  )
  const defaultOrganizationFoundingSource = useAppSelector(
    (state: RootState) => state.organization?.data?.settings?.defaultFundingSource
  )
  const [pageInfo, setPageInfo] = useState(DEFAULT_PAGE_INFO)
  const [hasMoreDrugs, setHasMoreDrugs] = useState(false)

  const selectedPrescribedItem = prescribedItems[selectedPrescribedItemIndex ?? -1]

  const dispatch = useAppDispatch()
  const toast = useToast()

  const { trigger: getProductDetails } = useApi(ProductSearchClient.getProductDetails, {
    onSuccess: (resp) => dispatch(selectProduct(resp.product)),
  })

  const { selectedPatient } = useAppSelector((state) => state.patientDetail)

  // Check if product contains S8 controlled substance
  const checkS8Restriction = (poisonClasses?: string[]): boolean => {
    if (!isAbleToSelectS8Medicine && poisonClasses?.includes('S8')) {
      toast({
        title: 'Warning',
        description: 'Selection of S8 controlled substance is prohibited.',
        status: 'warning',
        duration: null,
        isClosable: true,
        position: 'top',
      })
      return false
    }
    return true
  }

  const createAndAddPrescription = async (product: ProductResult) => {
    try {
      const response = await getProductDetails({
        id: product.productId,
        productType: product.productType,
      })

      if (!response) {
        console.error('No response returned')
        return
      }

      // Check S8 restriction
      if (!checkS8Restriction(response?.product?.poisonClassification?.poisonClasses)) {
        return
      }

      const item = detailsToPrescribedItem(response?.product, {}, selectedPatient?.ctg_flag)

      const { passed, message } = preCheckProduct(item.productDetails)
      if (message) {
        toast({
          title: passed ? 'Warning' : 'Error',
          description: message,
          status: passed ? 'warning' : 'error',
          duration: null,
          isClosable: true,
          position: 'top',
        })
      }
      if (!passed) {
        return
      }

      dispatch(selectProduct(undefined))
      dispatch(resetProductResults())
      dispatch(setSearchTerm(''))
      onUpdate(item, selectedPrescribedItemIndex)
      if (item && defaultOrganizationFoundingSource !== FundingSource.PRIVATE) {
        toggleDrawer?.()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const searchAndSelectProduct = async (product: ProductResult) => {
    try {
      const response = await getProductDetails({
        id: product.productId,
        productType: product.productType,
      })

      // Check S8 restriction
      if (!checkS8Restriction(response?.product?.poisonClassification?.poisonClasses)) {
        return
      }

      return dispatch(selectProduct(response?.product))
    } catch (e) {
      console.error(e)
    }
  }

  const { trigger: searchProducts } = useApi(ProductSearchClient.searchProduct, {
    onSuccess: (response) => {
      setHasMoreDrugs(response.products?.length > 0)
      dispatch(setProductResults(response.products))
    },
  })

  const searchProductsRef = useRef(searchProducts)
  searchProductsRef.current = searchProducts

  const drugSearch = useMemo(
    () =>
      debounce((queryTerm: string) => {
        if (queryTerm.length >= 3) {
          searchProductsRef.current({
            queryTerm,
            ...DEFAULT_PAGE_INFO,
          })
        }
      }, 500),
    []
  )

  useEffect(() => () => drugSearch.cancel(), [drugSearch])

  useEffect(() => {
    if (medication?.productId && !formErrors[medication.productId]) {
      setFormErrors((prev) => ({
        ...prev,
        [medication.productId]: initFormError,
      }))
    }
    // Only run when medication.productId changes to initialize form errors
  }, [medication?.productId, formErrors, setFormErrors])

  const loadMoreDrugs = useCallback(() => {
    const { pageNumber, pageOffset } = pageInfo
    const newPageInfo = { pageNumber: pageNumber + 1, pageOffset }
    setPageInfo(newPageInfo)
    searchProducts({
      queryTerm: productSearchTerm,
      ...newPageInfo,
    })
  }, [pageInfo, productSearchTerm, searchProducts])

  return (
    <Card p="16px">
      <VStack gap="16px" alignItems="stretch">
        {scid && <Text>SCID: {scid}</Text>}
        {!medication ? (
          <>
            <FormControl>
              <HStack justifyContent="start" alignItems="start">
                <FormLabel fontWeight="bold">Add Medications</FormLabel>
                <Image src={MimsIntegrated} alt="Mims Integrated" width="100px" />
              </HStack>
              <Input
                bgColor="white"
                placeholder="Search drugs ( at least 3 characters )"
                aria-label="Search drugs"
                value={productSearchTerm}
                disabled={isDeactivatedPrescriber}
                onChange={(e) => {
                  const queryTerm = e.target.value
                  dispatch(resetProductResults())
                  dispatch(setSearchTerm(queryTerm))
                  drugSearch(queryTerm)
                  setPageInfo(DEFAULT_PAGE_INFO)
                  if (selectedProduct) dispatch(selectProduct(undefined))
                }}
              ></Input>
            </FormControl>

            {productSearchTerm.length > 0 && (
              <DrugSearchResultsPanel
                hasMore={hasMoreDrugs}
                loadMoreResults={loadMoreDrugs}
                results={productResults}
                onAdd={async (product) => {
                  await createAndAddPrescription(product)
                }}
                onSelect={async (product) => {
                  toggleDrawer?.()
                  await searchAndSelectProduct(product)
                }}
                selectedId={selectedProduct?.productId}
              />
            )}
            <CustomPrescriptionItemModal
              onSelect={(item) => {
                const customPrescriptionItem = customPrescriptionItemToPrescribedItem(
                  item,
                  {},
                  selectedPatient?.ctg_flag
                )
                dispatch(selectProduct(undefined))
                return onUpdate(customPrescriptionItem, selectedPrescribedItemIndex)
              }}
              isOnlyPrivateMedicationAllowed={isOnlyPrivateMedicationAllowed}
            >
              {({ openModal }) => (
                <Box>
                  <Button
                    colorScheme="blue"
                    onClick={openModal}
                    variant="outline"
                    disabled={isDeactivatedPrescriber}
                  >
                    + Custom Preparation Items
                  </Button>
                </Box>
              )}
            </CustomPrescriptionItemModal>
          </>
        ) : (
          <>
            <MedicationSummaryCard medication={medication} toggleDrawer={toggleDrawer} />
            <PrescribedItemDetails
              prescribedItem={medication}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              onChange={(updatedItem) => {
                const selectedItemIndex = prescribedItems.findIndex(
                  (item) => selectedPrescribedItem === item
                )
                if (selectedItemIndex !== -1) {
                  onUpdate(updatedItem, selectedItemIndex)
                }
              }}
              onSelectPBSOrRPBS={() => toggleDrawer && toggleDrawer()}
              isOnlyPrivateMedicationAllowed={isOnlyPrivateMedicationAllowed}
            />
          </>
        )}
      </VStack>
    </Card>
  )
}

const MedicationSummaryCard = ({
  medication,
  toggleDrawer,
}: {
  medication: PrescribedItem
  toggleDrawer?: () => void
}) => {
  const dispatch = useAppDispatch()
  const selectedPrescribedItemIndex = useAppSelector(
    (state) => state.newScript.selectedPrescribedItemIndex
  )
  return (
    <ChakraCard
      bgColor="green.50"
      borderColor="green.400"
      borderWidth="1px"
      data-testid="selected-medication"
      padding="4"
      pr="0"
      borderRadius="md"
      position="relative"
      gap="2"
      cursor="pointer"
      onClick={() => {
        toggleDrawer?.()
      }}
    >
      <Flex gap="4" alignItems="center" flexWrap="wrap">
        <Badge variant="solid" colorScheme="green">
          SELECTED
        </Badge>
        <Text flex="1" fontWeight="bold">
          {medication.displayName}
        </Text>
        <IconButton
          position="absolute"
          right="0"
          top="0"
          zIndex="10"
          variant="unstyled"
          colorScheme="green"
          aria-label="Remove medication"
          icon={<SmallCloseIcon color="gray.500" />}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            dispatch(resetPrescribedItemByIndex(selectedPrescribedItemIndex))
          }}
        />
      </Flex>

      <Flex gap="4" color="gray.700">
        <Flex>
          <b>Qty</b>: <Text color="gray.500">{medication.quantity}</Text>
        </Flex>
        <Flex>
          <b>Rpts</b>: <Text color="gray.500">{medication.numberOfRepeatsAuthorised}</Text>
        </Flex>
        <Flex>
          <b>PBS Status</b>: <Text color="gray.500">{getPbsStatus(medication)}</Text>
        </Flex>
      </Flex>
    </ChakraCard>
  )
}

export default PrescribedItemList
